// src/pages/System.jsx
import { useState, useEffect } from 'react';
import { useGraphQL } from '../graphql';
import {
    CpuChipIcon,
    SignalIcon,
    ClockIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    XCircleIcon,
    ArrowPathIcon,
    Cog6ToothIcon,
    ServerIcon
} from '@heroicons/react/24/outline';

const System = () => {
    const { executeQuery, queries, loading, error } = useGraphQL();
    const [healthData, setHealthData] = useState([]);
    const [configData, setConfigData] = useState([]);
    const [activeTab, setActiveTab] = useState('health');
    const [lastRefresh, setLastRefresh] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadSystemData();
    }, []);

    const loadSystemData = async () => {
        try {
            const [health, config] = await Promise.all([
                executeQuery(queries.getSystemHealth()),
                executeQuery(queries.getSystemConfig())
            ]);

            setHealthData(health.systemHealth || []);
            setConfigData(config.systemConfig || []);
            setLastRefresh(new Date());
        } catch (err) {
            console.error('Failed to load system data:', err);
        }
    };

    const getStatusIcon = (status) => {
        const statusLower = status?.toLowerCase();
        switch (statusLower) {
            case 'healthy':
                return <CheckCircleIcon className="h-6 w-6 text-green-700" />;
            case 'degraded':
                return <ExclamationTriangleIcon className="h-6 w-6 text-yellow-700" />;
            case 'unhealthy':
                return <XCircleIcon className="h-6 w-6 text-red-700" />;
            default:
                return <SignalIcon className="h-6 w-6 text-gray-400" />;
        }
    };

    const getStatusColor = (status) => {
        const statusLower = status?.toLowerCase();
        switch (statusLower) {
            case 'healthy':
                return 'bg-green-500 bg-opacity-20 text-green-700 border-green-500';
            case 'degraded':
                return 'bg-yellow-500 bg-opacity-20 text-yellow-700 border-yellow-500';
            case 'unhealthy':
                return 'bg-red-500 bg-opacity-20 text-red-700 border-red-500';
            default:
                return 'bg-gray-500 bg-opacity-20 text-gray-400 border-gray-500';
        }
    };

    const getOverallStatus = () => {
        if (healthData.length === 0) return 'unknown';

        const hasUnhealthy = healthData.some(h => h.status?.toLowerCase() === 'unhealthy');
        const hasDegraded = healthData.some(h => h.status?.toLowerCase() === 'degraded');

        if (hasUnhealthy) return 'unhealthy';
        if (hasDegraded) return 'degraded';
        return 'healthy';
    };

    const getHealthStats = () => {
        const healthy = healthData.filter(h => h.status?.toLowerCase() === 'healthy').length;
        const degraded = healthData.filter(h => h.status?.toLowerCase() === 'degraded').length;
        const unhealthy = healthData.filter(h => h.status?.toLowerCase() === 'unhealthy').length;

        return { healthy, degraded, unhealthy, total: healthData.length };
    };

    const filteredConfig = configData.filter(config =>
        config.key?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        config.value?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const stats = getHealthStats();
    const overallStatus = getOverallStatus();

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                    <ServerIcon className="h-8 w-8 text-primary" />
                    <h1 className="text-2xl font-bold text-white">System Status</h1>
                </div>
                <button
                    onClick={loadSystemData}
                    className="px-4 py-2 bg-primary text-white hover:bg-opacity-90 flex items-center space-x-2"
                    disabled={loading}
                >
                    <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    <span>Refresh</span>
                </button>
            </div>

            {/* Overall Status Card */}
            <div className={` p-6 border-2 ${getStatusColor(overallStatus)}`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        {getStatusIcon(overallStatus)}
                        <div>
                            <h2 className="text-2xl font-bold capitalize">{overallStatus}</h2>
                            <p className="text-sm opacity-75">
                                {stats.healthy} of {stats.total} subsystems operational
                            </p>
                        </div>
                    </div>
                    {lastRefresh && (
                        <div className="text-right">
                            <div className="text-sm opacity-75">Last checked</div>
                            <div className="font-medium">{lastRefresh.toLocaleTimeString()}</div>
                        </div>
                    )}
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-green-500 bg-opacity-20 p-2 mr-3">
                            <CheckCircleIcon className="h-5 w-5 text-green-700" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Healthy</p>
                            <p className="text-xl font-semibold text-white">{stats.healthy}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-yellow-500 bg-opacity-20 p-2 mr-3">
                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-700" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Degraded</p>
                            <p className="text-xl font-semibold text-white">{stats.degraded}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-red-500 bg-opacity-20 p-2 mr-3">
                            <XCircleIcon className="h-5 w-5 text-red-700" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Unhealthy</p>
                            <p className="text-xl font-semibold text-white">{stats.unhealthy}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-700">
                <nav className="flex space-x-8">
                    <button
                        onClick={() => setActiveTab('health')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
 activeTab === 'health'
 ? 'border-primary text-primary'
 : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
 }`}
                    >
                        <div className="flex items-center space-x-2">
                            <CpuChipIcon className="h-5 w-5" />
                            <span>System Health</span>
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('config')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
 activeTab === 'config'
 ? 'border-primary text-primary'
 : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
 }`}
                    >
                        <div className="flex items-center space-x-2">
                            <Cog6ToothIcon className="h-5 w-5" />
                            <span>Configuration</span>
                        </div>
                    </button>
                </nav>
            </div>

            {/* Error Display */}
            {error && (
                <div className="bg-red-50 border border-red-200 p-4">
                    <p className="text-red-700">Error: {error}</p>
                </div>
            )}

            {/* Tab Content */}
            {activeTab === 'health' && (
                <div className="space-y-4">
                    {loading ? (
                        <div className="bg-gray-800 p-8 text-center">
                            <ArrowPathIcon className="h-8 w-8 text-primary animate-spin mx-auto mb-2" />
                            <p className="text-gray-400">Loading system health...</p>
                        </div>
                    ) : healthData.length === 0 ? (
                        <div className="bg-gray-800 p-8 text-center">
                            <ServerIcon className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                            <p className="text-gray-400">No health data available</p>
                        </div>
                    ) : (
                        healthData.map((subsystem, index) => (
                            <div key={index} className="bg-gray-800 p-4 hover:bg-gray-750 transition-colors">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start space-x-4 flex-1">
                                        {getStatusIcon(subsystem.status)}
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <h3 className="text-lg font-semibold text-white">
                                                    {subsystem.name}
                                                </h3>
                                                <span className={`px-2 py-1 text-xs font-medium border ${getStatusColor(subsystem.status)}`}>
                                                    {subsystem.status}
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-2 gap-4 text-sm mb-2">
                                                {subsystem.responseTimeMs !== null && subsystem.responseTimeMs !== undefined && (
                                                    <div className="flex items-center text-gray-400">
                                                        <ClockIcon className="h-4 w-4 mr-1" />
                                                        <span>Response: {subsystem.responseTimeMs}ms</span>
                                                    </div>
                                                )}
                                                <div className="flex items-center text-gray-400">
                                                    <SignalIcon className="h-4 w-4 mr-1" />
                                                    <span>Checked: {new Date(subsystem.lastChecked).toLocaleString()}</span>
                                                </div>
                                            </div>

                                            {subsystem.details && (
                                                <div className="mt-2 p-3 bg-gray-900 text-sm text-gray-300">
                                                    {subsystem.details}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {activeTab === 'config' && (
                <div className="space-y-4">
                    {/* Search */}
                    <div className="bg-gray-800 p-4">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search configuration..."
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        />
                    </div>

                    {loading ? (
                        <div className="bg-gray-800 p-8 text-center">
                            <ArrowPathIcon className="h-8 w-8 text-primary animate-spin mx-auto mb-2" />
                            <p className="text-gray-400">Loading configuration...</p>
                        </div>
                    ) : filteredConfig.length === 0 ? (
                        <div className="bg-gray-800 p-8 text-center">
                            <Cog6ToothIcon className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                            <p className="text-gray-400">
                                {searchTerm ? 'No configuration found matching your search' : 'No configuration data available'}
                            </p>
                        </div>
                    ) : (
                        <div className="bg-gray-800 overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-700">
                                <thead className="bg-gray-900">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        Key
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        Value
                                    </th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-700">
                                {filteredConfig.map((config, index) => (
                                    <tr key={index} className="hover:bg-gray-750 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <code className="text-sm font-mono text-primary">
                                                {config.key}
                                            </code>
                                        </td>
                                        <td className="px-6 py-4">
                                            <code className="text-sm font-mono text-gray-300 break-all">
                                                {config.value}
                                            </code>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {!loading && filteredConfig.length > 0 && (
                        <div className="text-sm text-gray-400 text-center">
                            Showing {filteredConfig.length} of {configData.length} configuration entries
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default System;